import { useState, useCallback, useRef } from "react";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  toolCalls: ToolCall[];
}

export interface ToolCall {
  id: string;
  name: string;
  input: unknown;
  result?: unknown;
}

export interface ChatContext {
  sessionId?: string;
  conversationId?: string;
  sourceKey?: string;
  sourcePartner?: string;
  referrer?: string;
  utmSource?: string;
  utmCampaign?: string;
}

export function useChatStream(apiUrl: string) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsStreaming(false);
  }, []);

  const sendMessage = useCallback(
    async (content: string, context: ChatContext) => {
      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content,
        toolCalls: [],
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsStreaming(true);

      // The assistant message is created lazily on the first stream event (see
      // applyToAssistant below). Until then the last message is the user's, so
      // MessageList's TypingIndicator gate (isLoading && last-role === "user") fires —
      // the "thinking" indicator during the dead-air window — and no empty assistant
      // bubble is rendered. An empty response or an abort-before-first-event therefore
      // leaves no assistant bubble at all.
      const assistantId = crypto.randomUUID();
      const applyToAssistant = (
        prev: ChatMessage[],
        update: (msg: ChatMessage) => ChatMessage,
      ): ChatMessage[] => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant" && last.id === assistantId) {
          return [...prev.slice(0, -1), update(last)];
        }
        const fresh: ChatMessage = {
          id: assistantId,
          role: "assistant",
          content: "",
          toolCalls: [],
        };
        return [...prev, update(fresh)];
      };

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const allMessages = [...messages, userMessage];
        const response = await fetch(`${apiUrl}/api/chat/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            messages: allMessages.map((m) => ({
              id: m.id,
              role: m.role,
              content: m.content,
            })),
            session_id: context.sessionId,
            conversation_id: context.conversationId,
            source_key: context.sourceKey,
            source_partner: context.sourcePartner,
            referrer: context.referrer,
            utm_source: context.utmSource,
            utm_campaign: context.utmCampaign,
          }),
          signal: abortController.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`Chat stream failed: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          // Aborting is not enough on its own: a chunk whose read() had already resolved when
          // stop() ran still resumes this loop, and applyToAssistant keys off the last message in
          // state — which by then may belong to a different conversation, or to none. Once this
          // send's own controller is aborted, its frames stop being applied.
          if (done || abortController.signal.aborted) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            // Same reason, per line: a single chunk can carry several frames, and the abort can
            // land between two of them.
            if (abortController.signal.aborted) break;
            if (!line.startsWith("data: ") || line === "data: [DONE]") continue;
            const event = JSON.parse(line.slice(6));

            switch (event.type) {
              case "text-delta":
                setMessages((prev) =>
                  applyToAssistant(prev, (m) => ({
                    ...m,
                    content: m.content + event.text_delta,
                  }))
                );
                break;
              case "tool-call":
                setMessages((prev) =>
                  applyToAssistant(prev, (m) => ({
                    ...m,
                    toolCalls: [
                      ...m.toolCalls,
                      {
                        id: event.tool_call_id,
                        name: event.tool_name,
                        input: event.tool_input,
                      },
                    ],
                  }))
                );
                break;
              case "tool-result":
                setMessages((prev) =>
                  applyToAssistant(prev, (m) => ({
                    ...m,
                    toolCalls: m.toolCalls.map((tc) =>
                      tc.id === event.tool_call_id
                        ? { ...tc, result: event.tool_result }
                        : tc
                    ),
                  }))
                );
                break;
            }
          }
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          // User cancelled — not an error
          return;
        }
        console.error("Chat stream error:", error);
      } finally {
        // Only the owning send may clear this state. If stop() aborted this stream and a newer
        // send has already installed its own controller, clearing here would strand that stream:
        // Stop would have no controller to abort, and isStreaming would read false while it is
        // still streaming. No path reaches that today — ChatInput gates Enter on !isLoading, so a
        // second send needs a Stop first, a separate event turn by which time this block has run —
        // but that reason lives in another package's component, so the invariant is asserted here
        // instead of inherited.
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
          setIsStreaming(false);
        }
      }
    },
    [apiUrl, messages]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return { messages, isStreaming, sendMessage, stop, clearMessages, setMessages };
}
